// ── 세무 자료 출력 유틸 ─────────────────────────────────────────────────────
// xlsx (세무사용) + 부가세 신고서 합계 계산
// 의존성: xlsx (npm install xlsx)

// V148: 엑셀 라이브러리(xlsx)는 내보내기를 누를 때만 받는다 — 첫 화면이 가벼워진다 (중국 망)
const loadXLSX = () => import('xlsx');
import {
  CATEGORY_MAJOR, CATEGORY_SUB,
  REVENUE_CATEGORIES, EVIDENCE_TYPES,
  categoryLabel, evidenceLabel, bizTypeLabel,
  isVATDeductible,
  pickPresumedRate, calcPresumedDeduction,
} from '../data/taxConstants';

const fmtNum = (n) => Math.round(Number(n) || 0);

// ── partner 캐시 lookup ─────────────────────────────────────────────────────
function partnerName(partners, partnerId, fallback) {
  if (fallback) return fallback;
  const p = (partners || []).find(x => x.id === partnerId);
  return p ? p.name : '';
}
function partnerBizNumber(partners, partnerId) {
  const p = (partners || []).find(x => x.id === partnerId);
  return p ? (p.bizNumber || '') : '';
}

// ── 세무사용 통합 xlsx ───────────────────────────────────────────────────────
// 시트 4장: 매출 / 매입 / 요약 / 메타
//
// 더존 Smart-A 임포트는 사무소마다 양식이 조금씩 달라서, 일단 표준 컬럼 헤더로
// 작성. 세무사 인터뷰 후 정확한 헤더로 교체 예정.
export async function exportTaxXlsx({
  transactions = [],
  partners = [],
  user,
  periodLabel = '전체 기간',
  bizName = '',
  bizNumber = '',
  annualRevenue = null,
  industry = 'food',
}) {
  const presumedRate = pickPresumedRate({ industry, annualRevenue });
  const XLSX = await loadXLSX();
  const wb = XLSX.utils.book_new();

  // ── 매출 시트 ──
  const salesRows = transactions
    .filter(t => t.type === 'income')
    .map(t => ({
      '거래일자': t.date,
      '거래처명': partnerName(partners, t.partnerId, t.partnerName),
      '거래처사업자번호': partnerBizNumber(partners, t.partnerId),
      '품목/적요': t.itemName || (t.revenueCategory || ''),
      '공급가액': fmtNum(t.supply),
      '부가세': fmtNum(t.vat),
      '합계': fmtNum(t.total),
      '증빙종류': evidenceLabel(t.evidence?.type),
      '적격증빙': t.evidence?.isQualified ? 'Y' : 'N',
      '매출구분': t.revenueCategory || '',
      '메모': t.memo || '',
      '입력출처': t.source || 'manual',
    }));
  if (salesRows.length === 0) salesRows.push({ '거래일자': '(매출 데이터 없음)' });
  const salesWS = XLSX.utils.json_to_sheet(salesRows);
  setColWidths(salesWS, [12, 22, 16, 30, 14, 12, 14, 14, 10, 12, 22, 10]);
  XLSX.utils.book_append_sheet(wb, salesWS, '매출');

  // ── 매입 시트 ──
  const expenseRows = transactions
    .filter(t => t.type === 'expense')
    .map(t => ({
      '거래일자': t.date,
      '거래처명': partnerName(partners, t.partnerId, t.partnerName),
      '거래처사업자번호': partnerBizNumber(partners, t.partnerId),
      '품목/적요': t.itemName || '',
      '공급가액': fmtNum(t.supply),
      '부가세': fmtNum(t.vat),
      '합계': fmtNum(t.total),
      '증빙종류': evidenceLabel(t.evidence?.type),
      '적격증빙': t.evidence?.isQualified ? 'Y' : 'N',
      '매입세액공제': isVATDeductible(t) ? 'Y' : 'N',
      '의제매입대상': t.vatTreatment?.presumed ? 'Y' : 'N',
      '대분류': CATEGORY_MAJOR.find(m => m.id === t.category?.major)?.label || '',
      '소분류': (CATEGORY_SUB[t.category?.major] || []).find(s => s.id === t.category?.sub)?.label || '',
      '사업/개인': t.incomeTreatment?.isPersonal ? '개인' : '사업',
      '메모': t.memo || '',
      '입력출처': t.source || 'manual',
    }));
  if (expenseRows.length === 0) expenseRows.push({ '거래일자': '(매입 데이터 없음)' });
  const expenseWS = XLSX.utils.json_to_sheet(expenseRows);
  setColWidths(expenseWS, [12, 22, 16, 30, 14, 12, 14, 14, 10, 12, 12, 12, 16, 10, 22, 10]);
  XLSX.utils.book_append_sheet(wb, expenseWS, '매입');

  // ── 요약 시트 ──
  const sums = computeSummary(transactions);
  const summaryRows = [
    { '항목': '대상 기간', '값': periodLabel },
    { '항목': '거래 건수 (총)', '값': transactions.length },
    { '항목': '거래 건수 (매출)', '값': sums.salesCount },
    { '항목': '거래 건수 (매입)', '값': sums.expenseCount },
    { '항목': '', '값': '' },
    { '항목': '── 매출 ──', '값': '' },
    { '항목': '매출 공급가액 합계', '값': fmtNum(sums.salesSupply) },
    { '항목': '매출 부가세 합계', '값': fmtNum(sums.salesVAT) },
    { '항목': '매출 합계 (부가세 포함)', '값': fmtNum(sums.salesTotal) },
    { '항목': '', '값': '' },
    { '항목': '── 매입 ──', '값': '' },
    { '항목': '매입 공급가액 합계', '값': fmtNum(sums.expenseSupply) },
    { '항목': '매입 부가세 합계', '값': fmtNum(sums.expenseVAT) },
    { '항목': '매입 합계 (부가세 포함)', '값': fmtNum(sums.expenseTotal) },
    { '항목': '매입세액공제 가능액 (부가세)', '값': fmtNum(sums.deductibleVAT) },
    { '항목': '의제매입대상 공급가액', '값': fmtNum(sums.presumedSupply) },
    { '항목': `의제매입공제 추정 (${presumedRate.label})`, '값': fmtNum(calcPresumedDeduction(sums.presumedSupply, presumedRate)) },
    { '항목': '', '값': '' },
    { '항목': '── 적격증빙 ──', '값': '' },
    { '항목': '적격증빙 매입액', '값': fmtNum(sums.qualifiedExpense) },
    { '항목': '비적격증빙 매입액', '값': fmtNum(sums.unqualifiedExpense) },
    { '항목': '적격증빙 비율', '값': sums.expenseSupply > 0 ? `${Math.round((sums.qualifiedExpense / sums.expenseSupply) * 100)}%` : '-' },
    { '항목': '비적격 가산세 위험 (2%)', '값': fmtNum(sums.unqualifiedExpense * 0.02) },
    { '항목': '', '값': '' },
    { '항목': '── 부가세 추정 ──', '값': '' },
    { '항목': '납부(환급) 부가세 추정', '값': fmtNum(sums.salesVAT - sums.deductibleVAT) },
    { '항목': '', '값': '' },
    { '항목': '── 손익 ──', '값': '' },
    { '항목': '이익 (공급가액 기준)', '값': fmtNum(sums.salesSupply - sums.expenseSupply) },
    { '항목': '', '값': '' },
    { '항목': '── 카테고리별 매입 ──', '값': '' },
    ...CATEGORY_MAJOR.flatMap(m => {
      const v = sums.byMajor[m.id] || 0;
      if (v <= 0) return [];
      return [{ '항목': `${m.emoji} ${m.label}`, '값': fmtNum(v) }];
    }),
  ];
  const summaryWS = XLSX.utils.json_to_sheet(summaryRows);
  setColWidths(summaryWS, [32, 22]);
  XLSX.utils.book_append_sheet(wb, summaryWS, '요약');

  // ── 메타 시트 ──
  const metaRows = [
    { '항목': '발행 일시', '값': new Date().toISOString().slice(0, 19).replace('T', ' ') },
    { '항목': '대상 기간', '값': periodLabel },
    { '항목': '사업자명', '값': bizName || '(미입력)' },
    { '항목': '사업자번호', '값': bizNumber || '(미입력)' },
    { '항목': '발행 도구', '값': '셈핏 (semfit) 세무 신고 도우미 V1' },
    { '항목': '주의사항', '값': '본 자료는 자동 분류 결과로, 최종 신고 전 세무사·본인 검토 필요. 의제매입공제 등은 적용 한도/요건 별도 확인.' },
  ];
  const metaWS = XLSX.utils.json_to_sheet(metaRows);
  setColWidths(metaWS, [16, 80]);
  XLSX.utils.book_append_sheet(wb, metaWS, '메타');

  // ── 다운로드 ──
  const fileName = `semfit_세무자료_${periodLabel.replace(/[^\d가-힣]/g, '_')}.xlsx`;
  XLSX.writeFile(wb, fileName);
  return fileName;
}

function setColWidths(ws, widths) {
  ws['!cols'] = widths.map(w => ({ wch: w }));
}

// ── 거래 합계 계산 (부가세 신고/요약/PDF에 공통 사용) ───────────────────────
export function computeSummary(transactions = []) {
  let salesSupply = 0, salesVAT = 0, salesTotal = 0, salesCount = 0;
  let expenseSupply = 0, expenseVAT = 0, expenseTotal = 0, expenseCount = 0;
  let deductibleVAT = 0;
  let presumedSupply = 0;
  let qualifiedExpense = 0, unqualifiedExpense = 0;
  const byMajor = {};
  const byEvidenceSales = {};
  const byEvidenceExpense = {};
  const byRevenueCategory = {};

  for (const t of transactions) {
    const supply = Number(t.supply) || 0;
    const vat = Number(t.vat) || 0;
    const total = Number(t.total) || 0;

    if (t.type === 'income') {
      salesSupply += supply; salesVAT += vat; salesTotal += total; salesCount++;
      const eid = t.evidence?.type || 'unknown';
      byEvidenceSales[eid] = (byEvidenceSales[eid] || 0) + supply;
      const rc = t.revenueCategory || 'unknown';
      byRevenueCategory[rc] = (byRevenueCategory[rc] || 0) + supply;
    } else {
      expenseSupply += supply; expenseVAT += vat; expenseTotal += total; expenseCount++;
      if (isVATDeductible(t)) deductibleVAT += vat;
      if (t.vatTreatment?.presumed) presumedSupply += supply;
      if (t.evidence?.isQualified) qualifiedExpense += supply;
      else unqualifiedExpense += supply;
      const M = t.category?.major || 'etc';
      byMajor[M] = (byMajor[M] || 0) + supply;
      const eid = t.evidence?.type || 'unknown';
      byEvidenceExpense[eid] = (byEvidenceExpense[eid] || 0) + supply;
    }
  }

  return {
    salesSupply, salesVAT, salesTotal, salesCount,
    expenseSupply, expenseVAT, expenseTotal, expenseCount,
    deductibleVAT, presumedSupply,
    qualifiedExpense, unqualifiedExpense,
    byMajor, byEvidenceSales, byEvidenceExpense, byRevenueCategory,
  };
}

// ── 부가세 신고서 미리채움 데이터 생성 ──────────────────────────────────────
// 일반과세자 신고서 기준. 칸 코드는 홈택스 신고서 기준.
export function buildVATReturnData(transactions = [], { annualRevenue = null, industry = 'food' } = {}) {
  const s = computeSummary(transactions);
  const presumedRate = pickPresumedRate({ industry, annualRevenue });

  // 매출 분류
  const salesByEvidence = s.byEvidenceSales;
  const taxInvoiceSales  = salesByEvidence['tax_invoice'] || 0;
  const cardSales        = salesByEvidence['card'] || 0;
  const cashReceiptSales = salesByEvidence['cash_receipt'] || 0;
  const otherSales       = (salesByEvidence['simple_receipt'] || 0)
                         + (salesByEvidence['paper_receipt']  || 0)
                         + (salesByEvidence['none']           || 0)
                         + (salesByEvidence['unknown']        || 0);

  // 매입 분류
  const expByEvidence = s.byEvidenceExpense;
  const taxInvoiceExpense  = expByEvidence['tax_invoice'] || 0;
  const cardExpense        = expByEvidence['card'] || 0;
  const cashReceiptExpense = expByEvidence['cash_receipt'] || 0;
  const presumedDeduction  = calcPresumedDeduction(s.presumedSupply, presumedRate);

  return {
    sales: {
      taxInvoice: { supply: taxInvoiceSales, vat: taxInvoiceSales * 0.1, label: '세금계산서 발급분 (1번 칸)' },
      cardCash:   { supply: cardSales + cashReceiptSales, vat: (cardSales + cashReceiptSales) * 0.1, label: '신용카드·현금영수증 발급분 (3번 칸)' },
      other:      { supply: otherSales, vat: otherSales * 0.1, label: '기타 매출 (4번 칸)' },
      total:      { supply: s.salesSupply, vat: s.salesVAT },
    },
    expense: {
      taxInvoice:    { supply: taxInvoiceExpense, vat: taxInvoiceExpense * 0.1, label: '세금계산서 수취분 (10번 칸)' },
      cardCash:      { supply: cardExpense + cashReceiptExpense, vat: (cardExpense + cashReceiptExpense) * 0.1, label: '카드·현금영수증 매입 (14번 칸)' },
      presumed:      { supply: s.presumedSupply, deduction: presumedDeduction, rate: presumedRate, label: `의제매입세액 (16번 칸, ${presumedRate.label})` },
      total:         { supply: s.expenseSupply, vat: s.expenseVAT, deductible: s.deductibleVAT },
    },
    payable: s.salesVAT - s.deductibleVAT - presumedDeduction,
    rawSummary: s,
  };
}
